import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getUserOrdersAPI } from "../../../../Services/checkoutService";
import useAuthStore from "../../../../Store/AuthStore";
import { Search, Calendar, ChevronRight } from "lucide-react";
import { formatEventDate } from "../../../../Utils/FormatEventDate";
import { useHistory } from "react-router-dom";
import { filterOrders } from "../../../../Utils/searchUtils";

const OrdersListPage = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { email } = useAuthStore();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        const fetchOrders = async () => {
            setLoading(true);
            const res = await getUserOrdersAPI(email); // assuming we can use email as identifier for now
            if (res.success) {
                setOrders(res.orders || []);
            }
            setLoading(false);
        };
        if (email) {
            fetchOrders();
        } else {
            setLoading(false);
        }
    }, [email]);

    const filteredOrders = useMemo(() =>
        filterOrders(orders, searchTerm),
        [orders, searchTerm]);

    return (
        <div className="flex flex-col min-h-[50vh] bg-grey-500 max-w-7xl mx-auto w-full">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold italic text-neutral-800">Your Orders</h2>
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search orders..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 rounded-full border border-neutral-200 focus:outline-none focus:border-red-400 text-sm w-64"
                    />
                    <Search className="w-4 h-4 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2" />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <div className="w-8 h-8 border-4 border-red-200 border-t-red-500 rounded-full animate-spin"></div>
                </div>
            ) : filteredOrders.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-[32px] border border-neutral-100 shadow-sm">
                    <p className="text-neutral-400 italic font-medium">No orders found.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredOrders.map((order) => (
                        <div
                            key={order._id}
                            onClick={() => history.push(`/profile/orders/${order._id}`)}
                            className="bg-white p-6 rounded-[24px] border border-neutral-100 shadow-sm hover:shadow-md hover:border-red-100 transition-all cursor-pointer group flex items-center justify-between"
                        >
                            <div className="flex items-center gap-6">
                                <div className="w-16 h-16 bg-neutral-50 rounded-2xl flex items-center justify-center flex-shrink-0 text-neutral-400 font-black text-xs uppercase tracking-widest border border-neutral-100">
                                    {new Date(order.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                </div>
                                <div>
                                    <h3 className="font-bold text-neutral-900 group-hover:text-red-500 transition-colors">
                                        Order #{order._id.slice(-8).toUpperCase()}
                                    </h3>
                                    <div className="flex items-center gap-4 mt-2 text-xs text-neutral-500">
                                        <span className="flex items-center gap-1.5 font-medium">
                                            <Calendar className="w-3.5 h-3.5" />
                                            {new Date(order.createdAt).toLocaleDateString()}
                                        </span>
                                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-neutral-300"></span>
                                        <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase font-bold tracking-widest ${order.status === 'completed' || order.status === 'paid' ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'}`}>
                                            {order.status || 'Pending'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-8">
                                <div className="text-right">
                                    <p className="text-sm font-black text-neutral-400 uppercase tracking-widest mb-1">Total</p>
                                    <p className="text-xl font-bold italic text-neutral-900">€{order.totalAmount?.toFixed(2)}</p>
                                </div>
                                <div className="w-10 h-10 rounded-full bg-neutral-50 flex items-center justify-center group-hover:bg-red-50 transition-colors">
                                    <ChevronRight className="w-5 h-5 text-neutral-400 group-hover:text-red-400 transition-colors" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default OrdersListPage;
